import React from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';

interface HelpModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const HelpModal: React.FC<HelpModalProps> = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-white z-50 overflow-y-auto">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Help Documentation</h1>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 transition-colors"
          >
            <XMarkIcon className="h-8 w-8" />
          </button>
        </div>

        {/* Table of Contents */}
        <div className="mb-12 bg-gray-50 p-6 rounded-lg">
          <h2 className="text-xl font-semibold mb-4">Table of Contents</h2>
          <nav className="space-y-2">
            <a href="#getting-started" className="block text-blue-600 hover:text-blue-800">1. Getting Started</a>
            <a href="#data-upload" className="block text-blue-600 hover:text-blue-800">2. Data Upload</a>
            <a href="#forecasting" className="block text-blue-600 hover:text-blue-800">3. Forecasting</a>
            <a href="#filters" className="block text-blue-600 hover:text-blue-800">4. Filters and Exclusions</a>
            <a href="#math" className="block text-blue-600 hover:text-blue-800">5. Math and Calculations</a>
          </nav>
        </div>

        {/* Content Sections */}
        <div className="space-y-12">
          <section id="getting-started" className="scroll-mt-20">
            <h2 className="text-2xl font-bold mb-4">1. Getting Started</h2>
            <p className="text-gray-700 mb-4">
              Welcome to the Sales Forecasting Tool. This tool helps you analyze historical sales data and generate accurate forecasts for future sales.
            </p>
            <p className="text-gray-700">
              To get started, you can either use the pre-loaded sample data or upload your own CSV file with sales data.
            </p>
          </section>

          <section id="data-upload" className="scroll-mt-20">
            <h2 className="text-2xl font-bold mb-4">2. Data Upload</h2>
            <p className="text-gray-700 mb-4">
              The tool accepts CSV files with the following required columns:
            </p>
            <ul className="list-disc pl-6 mb-4 text-gray-700">
              <li>SKU - Product identifier</li>
              <li>Product Name - Name of the product</li>
              <li>Period - Date in YYYY-MM format</li>
              <li>Sales Quantity - Number of units sold</li>
              <li>Sales Revenue - Total revenue in dollars</li>
              <li>Platform - Sales platform (e.g., Shopify, Amazon)</li>
            </ul>
          </section>

          <section id="forecasting" className="scroll-mt-20">
            <h2 className="text-2xl font-bold mb-4">3. Forecasting</h2>
            <p className="text-gray-700 mb-4">
              The tool provides two types of forecasts:
            </p>
            <ul className="list-disc pl-6 mb-4 text-gray-700">
              <li>Standard Forecast - Based on historical averages and growth rates</li>
              <li>Smoothed Forecast - Uses exponential smoothing to reduce noise in the data</li>
            </ul>
          </section>

          <section id="filters" className="scroll-mt-20">
            <h2 className="text-2xl font-bold mb-4">4. Filters and Exclusions</h2>
            <p className="text-gray-700 mb-4">
              You can improve forecast accuracy by:
            </p>
            <ul className="list-disc pl-6 mb-4 text-gray-700">
              <li>Excluding Dead SKUs - Products with no sales in the past 60 days</li>
              <li>Excluding Outliers - Products with unusual sales patterns</li>
              <li>Using Date Range Filters - Focus on specific time periods</li>
              <li>Searching Products - Find specific SKUs or product names</li>
            </ul>
          </section>

          <section id="math" className="scroll-mt-20">
            <h2 className="text-2xl font-bold mb-4">5. Math and Calculations</h2>
            
            <div className="space-y-6">
              <div className="bg-gray-50 p-6 rounded-lg">
                <h3 className="text-xl font-semibold mb-3">Standard Forecast</h3>
                <p className="text-gray-700 mb-2">Formula:</p>
                <code className="block bg-white p-3 rounded mb-2">
                  Forecast = Historical Average × (1 + Growth Rate)
                </code>
                <p className="text-gray-700">
                  Where Historical Average is the mean of sales for the same month across previous years.
                </p>
              </div>

              <div className="bg-gray-50 p-6 rounded-lg">
                <h3 className="text-xl font-semibold mb-3">Smoothed Forecast</h3>
                <p className="text-gray-700 mb-2">Formula:</p>
                <code className="block bg-white p-3 rounded mb-2">
                  Smoothed Value = α × Current Value + (1 - α) × Previous Smoothed Value
                </code>
                <p className="text-gray-700 mb-2">Where:</p>
                <ul className="list-disc pl-6 text-gray-700">
                  <li>α (alpha) = 0.3 (smoothing factor)</li>
                  <li>Current Value = Actual sales for the current period</li>
                  <li>Previous Smoothed Value = Smoothed value from the previous period</li>
                </ul>
              </div>

              <div className="bg-gray-50 p-6 rounded-lg">
                <h3 className="text-xl font-semibold mb-3">Year-over-Year (YOY) Change</h3>
                <p className="text-gray-700 mb-2">Formula:</p>
                <code className="block bg-white p-3 rounded mb-2">
                  YOY Change = (Forecast - Historical Average) / Historical Average × 100%
                </code>
                <p className="text-gray-700">
                  This shows the percentage difference between the forecast and historical average.
                </p>
              </div>

              <div className="bg-gray-50 p-6 rounded-lg">
                <h3 className="text-xl font-semibold mb-3">Revenue Calculations</h3>
                <p className="text-gray-700 mb-2">Formula:</p>
                <code className="block bg-white p-3 rounded mb-2">
                  Revenue = Quantity × Average Price
                </code>
                <p className="text-gray-700">
                  Where Average Price is calculated from historical revenue data.
                </p>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}; 